import { defineStore } from "pinia";
import { computed, ref } from "vue";

/**
 * Defaults to "To date": the designs disagree with each other on the default, so there is none to
 * match, and showing everything is the safer choice. A firm whose last lead arrived five weeks ago
 * would otherwise open to an empty page and read it as broken.
 */
export const DEFAULT_RANGE_ID = "to_date";

/** The viewer's calendar day, `YYYY-MM-DD` — local, never `toISOString()` (that is UTC). */
function localDayKey(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/** How often an open tab checks whether the day has turned. Cheap: a string compare. */
const DAY_CHECK_MS = 60_000;

/**
 * The calendar day, as ONE module-level clock rather than one per store instance. The clock is
 * global — there is one "today" per page — and registering the interval and the
 * `visibilitychange` listener inside the store leaked another pair for every `createPinia()`
 * (each test file builds dozens), which fake timers would then all drive at once.
 */
const dayKey = ref(localDayKey(new Date()));
function checkDay(): void {
  const today = localDayKey(new Date());
  if (today !== dayKey.value) dayKey.value = today;
}
let clockStarted = false;
function startClock(): void {
  if (clockStarted || typeof window === "undefined") return;
  clockStarted = true;
  window.setInterval(checkDay, DAY_CHECK_MS);
  document.addEventListener("visibilitychange", checkDay);
}

/**
 * The referring portal's reporting-period selection, shared by every page that narrows by date
 * (RAP-677). The ids are `INSIGHT_RANGES`' in `views/referring-portal/insightsFilters.ts`.
 *
 * Each page used to hold its own `ref("to_date")`, so a firm that picked "7 days" on Summary and
 * opened Leads was back on "To date" — and two screens side by side were quietly answering for two
 * different periods.
 *
 * In-memory, like `tableStateStore`: survives route navigation, resets on a reload. A reload is a
 * fresh look at the portal, and a stale "Yesterday" restored from storage days later would answer
 * a question nobody asked.
 *
 * ## `now` turns over at midnight
 *
 * Every window is computed from "now", inside `computed`s that otherwise depend only on the range
 * and the rows — so a tab left open past midnight kept serving yesterday as "Today". `now` is a
 * reactive clock that changes once per calendar day (checked every minute, and whenever the tab
 * becomes visible again), and every window reads it, so the pills re-derive when the day turns.
 */
export const usePortalRangeStore = defineStore("portalRange", () => {
  const rangeId = ref(DEFAULT_RANGE_ID);

  startClock();

  /** The current instant, re-read whenever the calendar day changes. Pass it to `rangeParams`. */
  const now = computed(() => {
    void dayKey.value;
    return new Date();
  });

  /** Back to the default range on sign-out (`clearAllStores`). */
  function $reset(): void {
    rangeId.value = DEFAULT_RANGE_ID;
  }

  return { $reset, checkDay, dayKey, now, rangeId };
});
